import { tw } from 'twind';

import { usePdp } from 'hooks/usePdp';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { ProductPromotions } from 'ui/components/ProductPromotions';
import { SafeHtml } from 'ui/elements/SafeHtml';
import { timezoneDateFormat } from 'utils/dateCalculations';
import { useTranslate } from 'hooks/useTranslations';
import { Maybe, Variant } from '__generated__/graphql';
import { useFeature } from 'hooks/useFeature';
import { isAppExclusiveValidRange } from 'utils/isAppExclusiveValidRange';

export const ProductMessages = () => {
  const useVariantPromotionExclusion = useFeature(
    'USE_VARIANT_PROMOTION_EXCLUSION'
  );

  const showPromoExclusionMsgInGray = useFeature(
    'SHOW_PROMO_EXCLUSION_MSG_IN_GRAY'
  );

  const { masterProduct, variation, isAppOnly } = usePdp();

  const t = useTranslate();

  return (
    <>
      {((useVariantPromotionExclusion && variation?.promotionExclusion) ||
        masterProduct?.promotionExclusion) && (
        <span className={tw(showPromoExclusionMsgInGray && 'text-puma-gray')}>
          {t('promotionExclusion')}
        </span>
      )}
      <ProductPromotions variant={variation} />
      {variation?.specialMessage &&
        (!isAppOnly ||
          isAppExclusiveValidRange(
            isAppOnly,
            variation?.appOnlyDateTimeFrom,
            variation?.appOnlyDateTimeTo
          )) && <SpecialMessage variant={variation} />}
      <OutOfStockMessage variant={variation} />
    </>
  );
};
const SpecialMessage = (props: { variant: Variant }) => (
  <SafeHtml
    as="span"
    data-test-id="product-special-message-text"
    html={props.variant.specialMessage!}
    className="border-b pb-6"
  />
);

const OutOfStockMessage = (props: { variant?: Maybe<Variant> }) => {
  const {
    locale,
    staticFeatures: { showTimeZoneName },
  } = useSiteConfig();
  const t = useTranslate();

  // Do not show the availability date if there is no `displayOutOfStock`,
  // or if `comingsoon` is false, or there is no `validTo` date provided
  const displayOutOfStock = props.variant?.displayOutOfStock;
  if (
    !displayOutOfStock ||
    !displayOutOfStock.comingsoon ||
    !displayOutOfStock.validTo
  )
    return null;

  const formattedDate = timezoneDateFormat(
    displayOutOfStock.validTo,
    locale,
    showTimeZoneName
  );

  // For JP change 12AM for 0AM
  const adjustedFormattedDate = formattedDate.replace('午前12時', '午前0時');

  return (
    <div
      className="text-lg font-bold border-b pb-6"
      data-test-id="product-availability-message-text"
    >
      {t('availableToOrderOn', {
        date: adjustedFormattedDate,
      })}
    </div>
  );
};
